// src/lib/optOutCheck.js
// Client-side opt-out check + branded "Re-add anyway?" modal.
// Used on every lead-create path (NewLeadModal, SubmitLead public
// form via Cloud Function, manual entry) so an agent never silently
// adds a seller who's told us to stop.
import React, { useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const BURG = '#6B1E2E';
const BURG_DK = '#4A1220';
const GOLD = '#C9A84C';
const CREAM = '#FAF7F2';
const BORDER = 'rgba(107,30,46,0.1)';
const MID = '#5A5A5A';
const LIGHT = '#9A9A9A';

// Lookup. Returns the doc data if the number is opted out (and not
// yet re-added), or null. Catches permission errors silently so the
// add-flow isn't blocked by a Firestore rule misconfig.
export async function checkOptOut(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '').replace(/^1?/, '');
  if (!digits) return null;
  try {
    const snap = await getDoc(doc(db, 'optedOutNumbers', digits));
    if (!snap.exists()) return null;
    const d = snap.data();
    if (d.reAddedAt) return null; // already re-cleared
    return { id: digits, ...d };
  } catch {
    return null;
  }
}

// Modal — admin types a confirmation note ("Verbal yes from seller
// on phone 5/12") to add anyway. Contract agents can't override; they
// see a read-only "this number is locked" message + Cancel.
export function OptOutWarningModal({ optOut, isAdmin, onCancel, onOverride }) {
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const date = optOut.optedOutAt?.toDate
    ? optOut.optedOutAt.toDate().toLocaleDateString()
    : (typeof optOut.optedOutAt === 'number' ? new Date(optOut.optedOutAt).toLocaleDateString() : 'a previous date');

  const submit = async () => {
    if (!confirmation.trim() || confirmation.trim().length < 10) return;
    setBusy(true);
    try { await onOverride(confirmation.trim()); } finally { setBusy(false); }
  };

  return (
    <div style={s.backdrop} onClick={() => !busy && onCancel()}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>
        <div style={s.head}>
          <div style={s.eyebrow}>⚠ Number opted out</div>
          <button onClick={onCancel} style={s.x} disabled={busy} aria-label="Close">×</button>
        </div>
        <div style={{ padding: 22 }}>
          <p style={{ margin: '0 0 14px', color: BURG_DK, fontSize: 14, lineHeight: 1.6 }}>
            <strong>{optOut.name || 'This seller'}</strong> at <span style={{ fontFamily: 'monospace' }}>{optOut.e164 || `+1${optOut.phone}`}</span>
            {' '}requested via SMS on <strong>{date}</strong> to stop being contacted.
          </p>
          <div style={s.quote}>
            <div style={s.quoteLabel}>Original opt-out message</div>
            <div style={s.quoteBody}>"{optOut.messageText || optOut.matchedKeyword || 'STOP'}"</div>
          </div>
          <p style={{ margin: '14px 0 0', fontSize: 13, color: MID, lineHeight: 1.65 }}>
            <strong style={{ color: BURG_DK }}>Carrier note:</strong> US carriers automatically block ALL SMS to this
            number from your business line — that block stays in place until the seller texts <strong>START</strong>
            to your number themselves. Re-adding here only clears HomeIQa's internal block.
          </p>

          {isAdmin ? (
            <>
              <p style={{ margin: '18px 0 8px', fontSize: 13, color: BURG_DK, fontWeight: 500 }}>
                Confirm the seller has explicitly told you they want to be contacted again:
              </p>
              <textarea
                value={confirmation}
                onChange={e => setConfirmation(e.target.value)}
                rows={3}
                placeholder='e.g. "Verbal yes from seller during call on 5/12 — they reconsidered selling."'
                style={s.input}
              />
              <div style={{ fontSize: 11, color: LIGHT, marginTop: 6 }}>
                This note is saved to the audit log alongside your name and timestamp.
              </div>
            </>
          ) : (
            <div style={s.notAdmin}>
              Only the owner / admin can override an opt-out. If the seller verbally agreed to be re-contacted,
              ask the owner to clear the opt-out in Settings → SMS opt-outs.
            </div>
          )}
        </div>
        <div style={s.footer}>
          <button onClick={onCancel} style={s.btnGhost} disabled={busy}>Cancel</button>
          {isAdmin && (
            <button
              onClick={submit}
              style={confirmation.trim().length >= 10 ? s.btnDanger : { ...s.btnDanger, opacity: 0.5, cursor: 'not-allowed' }}
              disabled={busy || confirmation.trim().length < 10}
            >
              {busy ? 'Saving…' : '✓ Confirm and re-add'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.55)', backdropFilter: 'blur(2px)', zIndex: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  panel: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 540, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" },
  head: { padding: '14px 22px', background: '#FEF2F2', borderBottom: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  eyebrow: { fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#991B1B' },
  x: { background: 'transparent', border: 'none', fontSize: 26, lineHeight: 1, color: '#991B1B', cursor: 'pointer', padding: 0, width: 28 },
  quote: { background: CREAM, border: `1px solid ${BORDER}`, borderLeft: '3px solid #991B1B', borderRadius: '0 8px 8px 0', padding: '10px 14px' },
  quoteLabel: { fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: LIGHT, marginBottom: 4, fontWeight: 500 },
  quoteBody: { fontSize: 13, color: '#1C1C1C', fontStyle: 'italic', lineHeight: 1.5 },
  input: { width: '100%', padding: '12px 14px', border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif", background: '#fff', color: '#1C1C1C', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  notAdmin: { marginTop: 16, padding: '12px 14px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, fontSize: 12, color: '#92400E', lineHeight: 1.6 },
  footer: { padding: '14px 22px', borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 10 },
  btnGhost: { background: 'transparent', color: BURG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  btnDanger: { background: '#fff', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 18px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }
};
